import { Add, Block, Delete } from '@mui/icons-material';
import { Box, Button, Divider, Grid } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { getAllInvoiceArticle } from 'src/shared/services/invoiceArticleService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { InvoiceArticle } from 'src/modules/invoice/models/invoiceArticle';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { disableEventTicket, enableEventTicket, getAllEventTicketsByEventId } from 'src/shared/services/eventTicketService';
import { toast } from 'react-toastify';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { TicketArticlePicker } from './ticketArticlePicker';

type TicketRowProps = {
  index: number;
  t: (k: string, o?: any) => string;
  canRemove: boolean;
  onRemove: (idx: number) => void;
  invoiceArticles: InvoiceArticle[];
  isEnabled: boolean;
  loadingTicketStatus: boolean;
  disableTicket: () => void;
  enableTicket: () => void;
  editMode?: boolean;
  deleteableWhileEdit?: boolean;
};

const TicketRow: React.FC<TicketRowProps> = React.memo(
  ({
    index,
    t,
    canRemove,
    onRemove,
    invoiceArticles,
    isEnabled,
    loadingTicketStatus,
    disableTicket,
    enableTicket,
    editMode = undefined,
    deleteableWhileEdit = undefined,
  }) => {
    const showDeleteButton = !editMode || (editMode && deleteableWhileEdit);

    return (
      <Box
        sx={{
          mb: 2,
          p: 2.5,
          borderRadius: 2,
          bgcolor: isEnabled ? 'background.paper' : 'grey.200',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 0.2,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ControlledTextField name={`tickets.${index}.ticketName`} required label={t('event:name')} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={6} md={2.5}>
            <ControlledTextField
              name={`tickets.${index}.price`}
              required
              label={t('event:formFields.fee')}
              inputMode='numeric'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} md={2.5}>
            <ControlledTextField
              name={`tickets.${index}.memberPrice`}
              label={t('event:formFields.memberFee')}
              required
              inputMode='numeric'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TicketArticlePicker index={index} invoiceArticles={invoiceArticles} />
          </Grid>

          {showDeleteButton && (
            <Grid item xs={6} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Box>
                <Button
                  size='medium'
                  fullWidth
                  variant='outlined'
                  endIcon={<Delete />}
                  color='error'
                  disableElevation
                  onClick={() => onRemove(index)}
                  disabled={!canRemove}
                >
                  {t('common:button.delete')}
                </Button>
              </Box>
            </Grid>
          )}
          {!showDeleteButton && (
            <Grid item xs={6} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Box>
                <Button
                  size='medium'
                  fullWidth
                  endIcon={isEnabled && <Block />}
                  variant='outlined'
                  color={isEnabled ? 'warning' : 'primary'}
                  disableElevation
                  onClick={() => {
                    if (isEnabled) {
                      disableTicket();
                    } else {
                      enableTicket();
                    }
                  }}
                  disabled={loadingTicketStatus}
                >
                  {isEnabled ? t('event:disable') : t('event:enable')}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  },
);
TicketRow.displayName = 'TicketRow';

export type Ticket = {
  ticketId?: number;
  ticketName: string;
  price: number | undefined;
  memberPrice: number | undefined;
  enabledAt: string;
  createdById: number;
  deleteableWhileEdit?: boolean;
};

const DEFAULT_TICKET: Ticket = {
  ticketName: '',
  price: 0,
  memberPrice: 0,
  createdById: 0,
  enabledAt: DateHelper.getDateTime(new Date()),
};

type EventTicketsFieldArrayProps = {
  eventId?: number;
  editMode?: boolean;
};

export const EventTicketsFieldArray: React.FC<EventTicketsFieldArrayProps> = ({ eventId = undefined, editMode = undefined }) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { control } = useFormContext();
  const { currentUser } = useGlobals();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingTicketStatus, setLoadingTicketStatus] = useState<boolean>(false);
  const [invoiceArticles, setInvoiceArticles] = useState<InvoiceArticle[]>([]);
  const { setValue, getValues } = useFormContext();

  const { fields, append, remove, replace, update } = useFieldArray({
    control,
    name: 'tickets',
  });

  const tickets = useWatch({ control, name: 'tickets' }) as Ticket[] | undefined;
  const length = tickets?.length ?? fields.length ?? 0;
  const canRemove = length > 1;

  const handleAdd = useCallback(() => {
    if (editMode) {
      append({ ...DEFAULT_TICKET, deleteableWhileEdit: true, createdById: currentUser?.user_id || 0 }, { shouldFocus: true });
    } else {
      append({ ...DEFAULT_TICKET, createdById: currentUser?.user_id || 0 }, { shouldFocus: true });
    }
  }, [append, currentUser?.user_id, editMode]);

  const handleRemove = useCallback(
    (idx: number) => {
      const row = tickets?.[idx];
      const rowIsDeletableInEdit = !!row?.deleteableWhileEdit;

      if (!editMode) {
        if (length > 1) remove(idx);
        return;
      }

      if (rowIsDeletableInEdit) {
        remove(idx);
      }
    },
    [editMode, length, remove, tickets],
  );

  const enableTicket = useCallback(
    async (ticketId: number) => {
      setLoadingTicketStatus(true);
      try {
        const result = await enableEventTicket(ticketId);
        if (isMounted()) {
          const idx = (tickets ?? []).findIndex((ticket) => ticket.ticketId === ticketId);
          if (idx >= 0) {
            setValue(`tickets.${idx}.enabledAt`, result.data.enabledAt);
            const row = getValues(`tickets.${idx}`) as Ticket;
            update(idx, row);
          }
        }
      } catch (e) {
        if (isMounted()) {
          toast.error(<>{t('event:failedToEnableTicket')}</>);
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) setLoadingTicketStatus(false);
      }
    },
    [getValues, isMounted, setValue, t, tickets, update],
  );

  const disableTicket = useCallback(
    async (ticketId: number) => {
      setLoadingTicketStatus(true);
      try {
        const result = await disableEventTicket(ticketId);
        if (isMounted()) {
          const idx = (tickets ?? []).findIndex((ticket) => ticket.ticketId === ticketId);
          if (idx >= 0) {
            setValue(`tickets.${idx}.enabledAt`, result.data.enabledAt);

            const row = getValues(`tickets.${idx}`) as Ticket;
            update(idx, row);
          }
        }
      } catch (e) {
        if (isMounted()) {
          toast.error(<>{t('event:failedToDisableTicket')}</>);
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) setLoadingTicketStatus(false);
      }
    },
    [getValues, isMounted, setValue, t, tickets, update],
  );

  const fetchData = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const result = await getAllEventTicketsByEventId(eventId);
      if (isMounted()) {
        const mapped = result.data.map((x) => ({
          ticketId: x.id,
          ticketName: x.ticketName,
          price: x.price,
          memberPrice: x.memberPrice,
          enabledAt: x.enabledAt ?? null,
          createdById: x.createdById ?? 0,
          deleteableWhileEdit: false,
          createdAt: x.createdAt,
          event_id: x.event_id,
          invoiceArticleId: x.invoiceArticleId,
          invoiceArticleName: x.invoiceArticleName,
        }));
        replace(mapped);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [eventId, isMounted, replace]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllInvoiceArticle({ pageIndex: 0, pageSize: 1000, sortCol: 'name', sortDir: 'asc', filter: '' });
      if (isMounted()) {
        setInvoiceArticles(result.data.results);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchArticle();
    }
  }, [fetchArticle, isMounted]);

  return (
    <Grid item xs={12}>
      <LoadingOverlay loading={loading} />
      {fields.map((item, index) => {
        const rowDeleteable = !!tickets?.[index]?.deleteableWhileEdit;
        const isEnabled = !!tickets?.[index]?.enabledAt;
        const ticketId = tickets?.[index]?.ticketId;

        return (
          <React.Fragment key={item.id}>
            <TicketRow
              index={index}
              t={t}
              canRemove={canRemove}
              onRemove={handleRemove}
              invoiceArticles={invoiceArticles}
              editMode={editMode}
              loadingTicketStatus={loadingTicketStatus}
              deleteableWhileEdit={rowDeleteable}
              isEnabled={isEnabled}
              disableTicket={() => {
                if (ticketId) {
                  disableTicket(ticketId);
                }
              }}
              enableTicket={() => {
                if (ticketId) {
                  enableTicket(ticketId);
                }
              }}
            />

            {index < fields.length - 1 && (
              <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
                <Divider sx={{ mx: -0.5 }} />
              </Grid>
            )}
          </React.Fragment>
        );
      })}

      <GridWrapper>
        <Grid item xs={12} sx={{ display: 'flex' }}>
          <Button size='medium' variant='contained' endIcon={<Add />} onClick={handleAdd}>
            {t('common:button.add')}
          </Button>
        </Grid>
      </GridWrapper>
    </Grid>
  );
};
